import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ConfirmCloseCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

export function ConfirmCloseCampaignDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: ConfirmCloseCampaignDialogProps) {
  const [isClosing, setIsClosing] = useState(false);

  const handleConfirm = async () => {
    setIsClosing(true);
    try {
      await onConfirm();
      // Dialog será fechado pelo parent após sucesso
    } catch (error) {
      // Erro será tratado pelo parent (toast)
    } finally {
      setIsClosing(false);
    }
  };

  const loading = isLoading || isClosing;

  return (
    <Dialog open={open} onOpenChange={loading ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-[540px]" onPointerDownOutside={loading ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Encerrar campanha?
          </DialogTitle>
          <div className="pt-3 space-y-3 text-sm text-muted-foreground">
            <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Atenção:</strong> Esta ação é definitiva e não poderá ser desfeita.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <p className="font-medium text-foreground">Ao encerrar esta campanha:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Nenhuma nova resposta poderá ser enviada</li>
                <li>Os resultados serão consolidados</li>
                <li>Um resultado final OFICIAL será gerado</li>
              </ul>
            </div>

            <p className="text-sm">
              Após o encerramento, os dados serão utilizados para as próximas etapas da NR-1.
            </p>
          </div>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? "Encerrando..." : "Encerrar campanha"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
